import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, GraduationCap, ClipboardCheck, UserCheck, Trophy, HeartHandshake,
  ArrowRight, MessageCircle, Star, Quote, PlayCircle, CheckCircle2, Images, ChevronLeft, ChevronRight,
  Phone, MapPin, Search, BookOpen, RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import TopStars from "@/components/TopStars";
import PublicStarTeacher from "@/components/PublicStarTeacher";
import { Container, SectionHeader, Eyebrow } from "@/components/Section";
import { useEnquiry } from "@/context/EnquiryContext";
import { useSite } from "@/context/SiteContext";
import { api, resolveImg, waLink } from "@/lib/api";

const STRENGTHS = [
  { icon: Users, title: "Small Batches", desc: "Maximum 15–20 students per class ensuring a focused learning environment." },
  { icon: GraduationCap, title: "Experienced Teachers", desc: "Senior faculty with deep subject expertise and genuine passion for teaching." },
  { icon: ClipboardCheck, title: "Regular Tests", desc: "Weekly & monthly chapter tests, mock exams and continuous performance tracking." },
  { icon: UserCheck, title: "Individual Attention", desc: "Personalised doubt-solving sessions and customised study strategies for each student." },
  { icon: Trophy, title: "Proven Results", desc: "A consistent track record built since 2003 through dedicated coaching." },
  { icon: HeartHandshake, title: "Parent Support", desc: "Regular parent-teacher meetings, attendance tracking and progress reports." },
];

const VIDEO_TITLES = {
  dkm_QpJ4fZE: "Bad Debts, Provisions & Adjustments",
  "pMVI-OJhGkg": "Sacrifice Ratio, New Ratio & Admission of Partner",
  sVimozRqVbQ: "12th Economics: Utility",
  "80-mseDs9aI": "10th Geometry 1.4 (1)",
};

const HELP_STEPS = [
  { icon: Search, title: "Understand the difficulty", desc: "We find where your child is getting stuck." },
  { icon: BookOpen, title: "Explain and practise", desc: "Clear explanations, then guided practice." },
  { icon: ClipboardCheck, title: "Homework checks & daily tests", desc: "Regular checks keep learning on track." },
  { icon: RotateCcw, title: "Re-explain and retest", desc: "Weak topics are taught again and retested where needed." },
  { icon: Star, title: "Star rewards & parent updates", desc: "Stars for effort and progress; parents stay informed." },
];

const STATS = [
  { value: "2003", label: "Established" },
  { value: "Small", label: "Focused Batches" },
  { value: "All Stages", label: "Pre-Primary to Degree" },
  { value: "1:1", label: "Personal Attention" },
];

export default function Home() {
  const { openEnquiry } = useEnquiry();
  const { site } = useSite();
  const [courses, setCourses] = useState([]);
  const [results, setResults] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [videos, setVideos] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/results").then((r) => setResults(r.data.slice(0, 4))).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data.slice(0, 3))).catch(() => {});
    api.get("/videos").then((r) => setVideos(r.data.slice(0, 3))).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data.slice(0, 8))).catch(() => {});
  }, []);

  const wa = waLink(site.whatsapp, "Hello PTG, I would like to inquire about admission.");
  const tel = `tel:+${(site.phone || "+91 98333 88201").replace(/\D/g, "")}`;

  // Exactly six cards for the Life at PTG grid. Uses only real uploaded photos;
  // if fewer than six distinct photos exist, the strongest is reused to keep the grid balanced.
  const lifePhotos = useMemo(() => {
    const arr = gallery.slice(0, 6);
    let k = 0;
    while (arr.length < 6 && gallery.length > 0) { arr.push(gallery[k % gallery.length]); k += 1; }
    return arr;
  }, [gallery]);

  return (
    <div data-testid="home-page">
      <Seo title="Home" description="Prasad Tutor Group (PTG), tuition in Khar East, Mumbai since 2003. Nursery to Std 10, Commerce (Std 11 & 12) and degree accounting subjects, with focused support for Std 9 and 10." />

      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-950" data-testid="hero-section">
        <img src="https://images.pexels.com/photos/18870256/pexels-photo-18870256.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-900/40" />
        <Container className="relative py-20 sm:py-28 lg:py-32">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="inline-flex flex-wrap items-center gap-2 rounded-full bg-amber-500/15 border border-amber-400/30 px-4 py-1.5 text-sm font-bold text-amber-300" data-testid="hero-badge">
              <Star className="h-4 w-4 fill-amber-300" /> Established 2003 <span className="text-amber-200/60">·</span> <MapPin className="h-4 w-4" /> Khar East, Mumbai
            </span>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight" data-testid="hero-heading">
              Tuition in Khar East.<br />
              <span className="text-sky-400">Personal Attention.</span><br />
              Stronger Basics.
            </h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-2xl" data-testid="hero-copy">
              School tuition from Nursery to Std 10, Commerce for Std 11–12 and degree-level accounting subjects. Focused support for Std 9 and 10 through clear explanations, homework follow-up, daily tests and retests when needed.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3">
              <Button onClick={() => openEnquiry()} size="lg" className="bg-amber-500 hover:bg-amber-600 text-white font-bold h-12 px-8 text-base shadow-lg" data-testid="hero-enquiry-btn">
                Admission Enquiry <ArrowRight className="ml-1 h-5 w-5" />
              </Button>
              <a href={tel} data-testid="hero-call-link">
                <Button size="lg" variant="outline" className="h-12 px-6 text-base border-white/30 bg-white/5 text-white hover:bg-white hover:text-slate-900 w-full sm:w-auto" data-testid="hero-call-btn">
                  <Phone className="mr-1 h-5 w-5" /> Call {site.phone || "+91 98333 88201"}
                </Button>
              </a>
              <a href={wa} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-link">
                <Button size="lg" className="h-12 px-6 text-base bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto" data-testid="hero-whatsapp-btn">
                  <MessageCircle className="mr-1 h-5 w-5" /> WhatsApp
                </Button>
              </a>
            </div>
          </motion.div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
            {STATS.map((s, i) => (
              <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.1 }}
                className="rounded-2xl bg-white/10 backdrop-blur-sm border border-white/10 p-4 text-center" data-testid={`hero-stat-${i}`}>
                <div className="font-heading text-2xl sm:text-3xl font-extrabold text-white">{s.value}</div>
                <div className="text-xs sm:text-sm text-slate-300 mt-1">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* HOW WE HELP */}
      <section className="py-14 sm:py-16 bg-white border-b border-slate-100" data-testid="how-we-help-section">
        <Container>
          <SectionHeader eyebrow="Our Method" title="How we help your child" />
          <div role="list" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 pb-12 sm:pb-0">
            {HELP_STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.05}>
                <div role="listitem" className="relative h-full flex sm:block items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:p-5 hover:border-sky-300 hover:bg-white transition-colors" data-testid={`how-step-${i}`}>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-900 text-white text-sm font-extrabold">{i + 1}</span>
                    <s.icon className="hidden sm:block h-5 w-5 text-sky-600" />
                  </div>
                  <div>
                  <h3 className="sm:mt-3 font-bold text-slate-800 leading-snug">{s.title}</h3>
                  <p className="mt-1 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-16 sm:py-24 ptg-grid-bg" data-testid="why-choose-section">
        <Container>
          <SectionHeader eyebrow="Why Choose PTG" title="Built on trust, discipline and personal care"
            subtitle="Everything we do is designed to help your child learn with confidence and achieve their best." />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {STRENGTHS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <div className="group h-full bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:border-sky-300 transition-all duration-300 p-7" data-testid={`strength-${i}`}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-900 text-white group-hover:scale-110 transition-transform">
                    <s.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-slate-800">{s.title}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* COURSES */}
      <section className="py-16 sm:py-24 bg-slate-50" data-testid="home-courses-section">
        <Container>
          <SectionHeader eyebrow="Our Courses" title="Courses at Prasad Tutor Group"
            subtitle="From early learning to higher education, PTG provides focused coaching with personal attention at every stage." />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {courses.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.08}>
                <div className="h-full flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden" data-testid={`home-course-${i}`}>
                  <div className="bg-blue-900 p-6 text-white">
                    <h3 className="text-2xl font-bold">{c.title}</h3>
                    <p className="text-sky-200 text-sm mt-1">{c.subtitle}</p>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <p className="text-slate-600 text-sm leading-relaxed">{c.description}</p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      {(c.subjects || []).map((s) => (
                        <span key={s} className="rounded-full bg-sky-50 border border-sky-200 px-3 py-1 text-xs font-semibold text-sky-800">{s}</span>
                      ))}
                    </div>
                    <Button onClick={() => openEnquiry(c.title)} className="mt-6 w-full bg-blue-900 hover:bg-blue-950" data-testid={`home-course-enquire-${i}`}>
                      Enquire Now
                    </Button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/courses"><Button variant="outline" className="border-blue-900 text-blue-900 hover:bg-blue-50" data-testid="home-view-courses">View All Courses <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
          </div>
        </Container>
      </section>

      {/* RESULTS */}
      <section className="py-16 sm:py-24" data-testid="home-results-section">
        <Container>
          <SectionHeader eyebrow="Results & Achievements" title="Celebrating our students' success"
            subtitle="Explore genuine student progress and academic achievements from PTG." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {results.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.06}>
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden text-center hover:shadow-lg transition-all" data-testid={`home-result-${i}`}>
                  <div className="h-40 bg-gradient-to-br from-blue-900 to-sky-700 flex items-center justify-center overflow-hidden">
                    {r.photo ? <img src={resolveImg(r.photo)} alt={r.student_name} className="h-full w-full object-cover" />
                      : <GraduationCap className="h-14 w-14 text-white/70" />}
                  </div>
                  <div className="p-4">
                    <div className="inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-300 px-3 py-1 text-xs font-bold text-amber-900">
                      <Trophy className="h-3.5 w-3.5" /> {r.percentage || "—"}
                    </div>
                    <h4 className="mt-2 font-bold text-slate-800">{r.student_name}</h4>
                    <p className="text-xs text-slate-500">{r.standard} • {r.year}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/results"><Button variant="outline" className="border-blue-900 text-blue-900 hover:bg-blue-50" data-testid="home-view-results">See All Results <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
          </div>
        </Container>
      </section>

      {/* WHY FAMILIES CHOOSE PTG */}
      <TopStars />
      <PublicStarTeacher />

      <section className="py-16 sm:py-24 bg-blue-950 text-white" data-testid="home-trust-section">
        <Container>
          <SectionHeader eyebrow="Why PTG" title="Why Families Choose PTG" light
            subtitle="For more than two decades, PTG has focused on personal attention, stronger concepts, regular practice and consistent academic guidance." />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: UserCheck, title: "Personal Attention", desc: "Focused learning environments help teachers understand individual student needs." },
              { icon: HeartHandshake, title: "Stronger Concepts", desc: "We focus on clear fundamentals and concept-based learning before moving towards more challenging work." },
              { icon: ClipboardCheck, title: "Regular Practice", desc: "Consistent practice and guidance help students build confidence and progress step by step." },
              { icon: Trophy, title: "Support for Every Learner", desc: "From learners who need additional support to students aiming for higher achievement, PTG helps each student move forward." },
            ].map((t, i) => (
              <Reveal key={t.title} delay={i * 0.06}>
                <div className="h-full rounded-2xl bg-white/5 border border-white/10 p-7 backdrop-blur-sm" data-testid={`home-trust-${i}`}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-500/20 text-sky-300"><t.icon className="h-6 w-6" /></div>
                  <h3 className="mt-4 text-base font-extrabold uppercase tracking-wide">{t.title}</h3>
                  <p className="mt-2 text-slate-300 text-sm leading-relaxed">{t.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* COMMUNITY — More Than Tuition */}
      <section className="py-16 sm:py-24 bg-slate-50" data-testid="home-community-section">
        <Container>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <Reveal>
              <div>
                <Eyebrow>Our Community</Eyebrow>
                <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight">More Than Tuition. A Learning Community.</h2>
                <p className="mt-5 text-slate-600 leading-relaxed text-lg">
                  For more than two decades, Prasad Tutor Group has supported generations of students through personal attention, small batches, regular guidance and a positive learning environment.
                </p>
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <Button onClick={() => openEnquiry()} className="bg-amber-500 hover:bg-amber-600 text-white font-bold h-12 px-8" data-testid="community-enquiry-btn">Admission Enquiry <ArrowRight className="ml-1 h-5 w-5" /></Button>
                  <Link to="/about"><Button variant="outline" className="h-12 px-8 border-blue-900 text-blue-900 hover:bg-blue-50 w-full sm:w-auto" data-testid="community-about-btn">About PTG</Button></Link>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="relative">
                {site.community_photo ? (
                  <img src={resolveImg(site.community_photo)} alt="Students and teachers at Prasad Tutor Group, Khar East, Mumbai" className="w-full aspect-[4/3] object-cover rounded-3xl shadow-2xl" />
                ) : (
                  <div className="w-full aspect-[4/3] rounded-3xl border-2 border-dashed border-slate-300 bg-white flex flex-col items-center justify-center text-center p-8" data-testid="community-photo-placeholder">
                    <Images className="h-12 w-12 text-slate-300" />
                    <p className="mt-3 font-semibold text-slate-500">Group photograph coming soon</p>
                    <p className="text-sm text-slate-400 mt-1">The PTG team can add the main community photo from the admin panel.</p>
                  </div>
                )}
                <div className="absolute -bottom-5 -left-5 hidden sm:flex items-center gap-3 rounded-2xl bg-blue-900 text-white px-5 py-4 shadow-xl">
                  <Star className="h-6 w-6 fill-amber-400 text-amber-400" />
                  <div><div className="font-heading font-extrabold text-xl leading-none">Since 2003</div><div className="text-xs text-sky-300">Trusted by families</div></div>
                </div>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      {/* LIFE AT PTG */}
      <section className="py-16 sm:py-24" data-testid="home-life-section">
        <Container>
          <SectionHeader eyebrow="Life at PTG" title="Life at PTG"
            subtitle="Learning, growing and celebrating together since 2003." />
          {gallery.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {lifePhotos.map((g, i) => (
                <Reveal key={`${g.id}-${i}`} delay={(i % 3) * 0.06}>
                  <button onClick={() => setLightbox(i)} className="group relative block w-full overflow-hidden rounded-2xl aspect-[4/3] shadow-sm hover:shadow-xl transition-all duration-300" data-testid={`life-photo-${i}`}>
                    <img src={resolveImg(g.image)} alt={g.caption || "Prasad Tutor Group, Khar East, Mumbai"} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                      <p className="text-white text-xs font-medium text-left">{g.caption}</p>
                    </div>
                  </button>
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="rounded-3xl border-2 border-dashed border-slate-300 bg-slate-50 p-12 text-center" data-testid="life-empty">
              <Images className="mx-auto h-12 w-12 text-slate-300" />
              <p className="mt-3 font-semibold text-slate-600">Real PTG photographs coming soon</p>
              <p className="text-sm text-slate-400 mt-1 max-w-md mx-auto">Photos of classes, activities and celebrations will appear here once added from the admin panel.</p>
            </div>
          )}
          <div className="text-center mt-10">
            <Link to="/gallery"><Button variant="outline" className="border-blue-900 text-blue-900 hover:bg-blue-50" data-testid="home-view-gallery">View Full Gallery <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
          </div>
        </Container>
      </section>

      {/* VIDEOS */}
      {videos.length > 0 && (
        <section className="py-16 sm:py-24 bg-slate-50" data-testid="home-videos-section">
          <Container>
            <SectionHeader eyebrow="Latest Videos" title="Watch PTG in action" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {videos.map((v, i) => (
                <Reveal key={v.id} delay={i * 0.08}>
                  <a href={`https://www.youtube.com/watch?v=${v.youtube_id}`} target="_blank" rel="noreferrer" className="block group" data-testid={`home-video-${i}`}>
                    <div className="relative overflow-hidden rounded-2xl">
                      <img src={`https://img.youtube.com/vi/${v.youtube_id}/hqdefault.jpg`} alt={v.title} className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-slate-900/30 flex items-center justify-center">
                        <PlayCircle className="h-14 w-14 text-white/90 group-hover:scale-110 transition-transform" />
                      </div>
                    </div>
                    <h4 className="mt-3 text-sm sm:text-base font-semibold text-slate-800 leading-snug line-clamp-2">{VIDEO_TITLES[v.youtube_id] || v.title}</h4>
                  </a>
                </Reveal>
              ))}
            </div>
          </Container>
        </section>
      )}

      {/* CTA BAND */}
      <section className="py-16" data-testid="home-cta-band">
        <Container>
          <div className="rounded-3xl bg-blue-900 p-10 sm:p-14 text-center relative overflow-hidden">
            <div className="ptg-grid-bg absolute inset-0 opacity-20" />
            <div className="relative">
              <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-white">Ready to give your child the PTG advantage?</h2>
              <p className="mt-3 text-sky-200 max-w-xl mx-auto">Admissions are open for 2026–27. Seats per batch are limited to keep classes small and personal.</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <Button onClick={() => openEnquiry()} size="lg" className="bg-amber-500 hover:bg-amber-600 font-bold h-12 px-8" data-testid="cta-enquiry-btn">Admission Enquiry</Button>
                <Link to="/contact"><Button size="lg" variant="outline" className="h-12 px-8 border-white/40 bg-transparent text-white hover:bg-white hover:text-blue-900 w-full sm:w-auto" data-testid="cta-contact-btn">Contact Us</Button></Link>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* GOOGLE REVIEWS CTA */}
      {(site.maps_link || site.maps_embed) && (
        <section className="py-14 sm:py-16" data-testid="google-review-section">
          <Container>
            <div className="rounded-3xl bg-blue-950 text-white px-6 py-10 sm:px-12 sm:py-12 text-center">
              <h2 className="font-heading text-2xl sm:text-3xl font-extrabold">Happy with PTG?</h2>
              <p className="mt-3 text-slate-300 max-w-xl mx-auto">Your feedback helps other parents and students discover Prasad Tutor Group.</p>
              <a href={site.maps_link || site.maps_embed} target="_blank" rel="noreferrer" className="inline-block mt-7" data-testid="google-review-btn">
                <Button className="bg-amber-500 hover:bg-amber-600 text-white font-bold h-12 px-8">⭐ Review PTG on Google</Button>
              </a>
            </div>
          </Container>
        </section>
      )}

      {/* LIFE AT PTG LIGHTBOX */}
      <Dialog open={lightbox !== null} onOpenChange={(o) => !o && setLightbox(null)}>
        <DialogContent className="max-w-4xl bg-slate-950 border-slate-800 p-2 sm:p-4" data-testid="home-lightbox">
          {lightbox !== null && lifePhotos[lightbox] && (
            <div className="relative">
              <img src={resolveImg(lifePhotos[lightbox].image)} alt={lifePhotos[lightbox].caption || "Prasad Tutor Group, Khar East, Mumbai"} className="w-full max-h-[75vh] object-contain rounded-lg" />
              {lifePhotos[lightbox].caption && <p className="text-center text-slate-200 mt-3 text-sm">{lifePhotos[lightbox].caption}</p>}
              {lifePhotos.length > 1 && (
                <>
                  <button onClick={() => setLightbox((p) => (p - 1 + lifePhotos.length) % lifePhotos.length)} className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/20 p-2 text-white hover:bg-white/40" data-testid="home-lightbox-prev"><ChevronLeft className="h-6 w-6" /></button>
                  <button onClick={() => setLightbox((p) => (p + 1) % lifePhotos.length)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/20 p-2 text-white hover:bg-white/40" data-testid="home-lightbox-next"><ChevronRight className="h-6 w-6" /></button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
